// src/features/bookings/hooks/useBookingDetail.js
// يُغني BookingDetail.js عن كل منطق الجلب والحذف وتحديث الحالة
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  fetchBookingById,
  fetchSettings,
  updateBooking,
  updateBookingStatus,
  deleteBooking,
} from '../../../api';

const useBookingDetail = (id) => {
  const navigate = useNavigate();

  const [booking, setBooking]   = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');

  // ─── جلب البيانات ────────────────────────────────────────────
  const loadData = useCallback(async () => {
    if (!id) return;
    setError('');
    try {
      const [bookingRes, settingsRes] = await Promise.all([
        fetchBookingById(id),
        fetchSettings(),
      ]);
      setBooking(bookingRes.data);
      setSettings(settingsRes.data);
    } catch {
      setError('لا يمكن العثور على الحجز المطلوب.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ─── تحديث الحالة ────────────────────────────────────────────
  const changeStatus = useCallback(async (newStatus) => {
    try {
      await updateBookingStatus(id, newStatus);
      await loadData();
    } catch {
      setError('فشل تحديث حالة الحجز.');
    }
  }, [id, loadData]);

  // ─── حذف الحجز ───────────────────────────────────────────────
  const removeBooking = useCallback(async () => {
    try {
      await deleteBooking(id);
      navigate('/');
    } catch (err) {
      const errorData = err.response?.data;
      if (err.response?.status === 400 && errorData?.error) {
        setError(errorData.error);
      } else if (err.response?.status === 403) {
        setError('لا تملك الصلاحية لتنفيذ هذا الإجراء.');
      } else {
        setError('حدث خطأ غير متوقع أثناء حذف الحجز.');
      }
    }
  }, [id, navigate]);

  // ─── حفظ التعديلات ───────────────────────────────────────────
  const saveEdit = useCallback(async (editedData) => {
    const payload = {
      booking_date:      editedData.booking_date,
      shooting_location: editedData.shooting_location,
      shooting_time:     editedData.shooting_time,
      notes:             editedData.notes,
      graduation_details: editedData.graduation_details,
      wedding_details:    editedData.wedding_details,
      kids_details:       editedData.kids_details,
      event_details:      editedData.event_details,
    };
    if (editedData.booking_type !== 'graduation') {
      payload.total_price = editedData.total_price;
    }
    await updateBooking(id, payload);
    await loadData();
  }, [id, loadData]);

  return {
    booking,
    settings,
    loading,
    error,
    setError,
    loadData,
    changeStatus,
    removeBooking,
    saveEdit,
  };
};

export default useBookingDetail;